import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Copy, DollarSign, Eye, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `\u20B9${(Number(n) || 0).toFixed(2)}`;

const AdminAffiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', email: '', commission_percent: 10, active: true });
  const [viewing, setViewing] = useState(null); // affiliate whose commissions we're inspecting
  const [commissions, setCommissions] = useState([]);
  const [commLoading, setCommLoading] = useState(false);

  useEffect(() => { fetchAffiliates(); }, []);

  const fetchAffiliates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/affiliates`, { withCredentials: true });
      setAffiliates(data.affiliates || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load affiliates'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ code: '', name: '', email: '', commission_percent: 10, active: true });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ code: a.code, name: a.name, email: a.email || '', commission_percent: a.commission_percent, active: a.active });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/api/admin/affiliates/${editing.affiliate_id}`, {
          name: form.name, email: form.email, commission_percent: Number(form.commission_percent), active: form.active,
        }, { withCredentials: true });
        toast.success('Affiliate updated');
      } else {
        await axios.post(`${API}/api/admin/affiliates`, form, { withCredentials: true });
        toast.success('Affiliate created');
      }
      setDialogOpen(false);
      fetchAffiliates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this affiliate? Existing commissions remain intact.')) return;
    try {
      await axios.delete(`${API}/api/admin/affiliates/${id}`, { withCredentials: true });
      toast.success('Affiliate deactivated');
      fetchAffiliates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to deactivate'); }
  };

  const hardDelete = async (a) => {
    if (!window.confirm(`Permanently delete affiliate '${a.code}'?\n\nCommission and click history remain in the ledger for audit, but the affiliate row will be removed from this list.`)) return;
    try {
      await axios.delete(`${API}/api/admin/affiliates/${a.affiliate_id}?hard=true`, { withCredentials: true });
      toast.success(`Affiliate ${a.code} deleted`);
      fetchAffiliates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const exportCsv = async (affiliateId = null) => {
    try {
      const url = affiliateId
        ? `${API}/api/admin/commissions/export?affiliate_id=${encodeURIComponent(affiliateId)}`
        : `${API}/api/admin/commissions/export`;
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'rejumist-commissions.csv';
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`Downloaded ${filename}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to export CSV'); }
  };

  const openCommissions = async (a) => {
    setViewing(a);
    setCommLoading(true);
    setCommissions([]);
    try {
      const { data } = await axios.get(`${API}/api/admin/affiliates/${a.affiliate_id}/commissions`, { withCredentials: true });
      setCommissions(data.commissions || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load commissions'); }
    finally { setCommLoading(false); }
  };

  const markPaid = async (commissionId) => {
    try {
      await axios.put(`${API}/api/admin/commissions/${commissionId}/pay`, {}, { withCredentials: true });
      toast.success('Commission marked as paid');
      // Refresh both current list and outer stats
      if (viewing) openCommissions(viewing);
      fetchAffiliates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to mark paid'); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/products?ref=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(url);
    toast.success(`Referral link copied: ${url}`);
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST' : '—';

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-affiliates-page">
      <Header />
      <div className="container mx-auto px-4 py-10 pt-16">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-black font-heading" data-testid="admin-affiliates-heading">Affiliates</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => exportCsv()} data-testid="export-all-commissions-btn"><Download className="h-4 w-4 mr-1" /> Export Commissions CSV</Button>
            <Button onClick={openCreate} data-testid="new-affiliate-btn"><Plus className="h-4 w-4 mr-1" /> New Affiliate</Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E0E0E0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="affiliates-table">
              <thead className="bg-[#FAFAFA] border-b border-[#E0E0E0]">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 text-right">Clicks</th>
                  <th className="px-4 py-3 text-right">Orders</th>
                  <th className="px-4 py-3 text-right">Total Sales</th>
                  <th className="px-4 py-3 text-right">Commission</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground" data-testid="no-affiliates">No affiliates yet. Create one to get started.</td></tr>
                )}
                {affiliates.map((a) => (
                  <tr key={a.affiliate_id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]" data-testid={`affiliate-row-${a.code}`}>
                    <td className="px-4 py-3 font-mono font-semibold">{a.code}</td>
                    <td className="px-4 py-3">{a.name}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{a.email || '—'}</td>
                    <td className="px-4 py-3 text-right">{a.clicks}</td>
                    <td className="px-4 py-3 text-right">{a.orders}</td>
                    <td className="px-4 py-3 text-right font-medium">{fmtINR(a.total_sales)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-bold">{fmtINR(a.total_commission)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        Pending {fmtINR(a.pending_commission)} · Paid {fmtINR(a.paid_commission)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${a.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {a.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyLink(a.code)} title="Copy referral link" data-testid={`copy-link-${a.code}`}><Copy className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openCommissions(a)} title="View commissions" data-testid={`view-commissions-${a.code}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => exportCsv(a.affiliate_id)} title="Export this affiliate's CSV" data-testid={`export-${a.code}`}><Download className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)} title="Edit" data-testid={`edit-${a.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        {a.active && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deactivate(a.affiliate_id)} title="Deactivate" data-testid={`deactivate-${a.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => hardDelete(a)} title="Delete permanently" data-testid={`delete-${a.code}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : 'New Affiliate'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="affiliate-form">
              <div>
                <Label>Code * (letters, digits, - or _)</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required disabled={!!editing} placeholder="e.g., ANIKA" data-testid="affiliate-code-input" />
              </div>
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="affiliate-name-input" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="affiliate-email-input" /></div>
              <div>
                <Label>Commission %</Label>
                <Input type="number" min="0" max="100" step="0.1" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} data-testid="affiliate-pct-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="affiliate-active-toggle" />
                <Label>Active</Label>
              </div>
              <Button type="submit" className="w-full" data-testid="save-affiliate-btn">{editing ? 'Update' : 'Create'}</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Commissions Dialog */}
        <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between pr-6">
                <span>Commissions for {viewing?.code}</span>
                {viewing && commissions.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => exportCsv(viewing.affiliate_id)} data-testid="export-affiliate-commissions-btn"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto" data-testid="commissions-list">
              {commLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : commissions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="no-commissions">No commissions yet.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA]">
                    <tr className="text-left uppercase text-[10px] font-bold text-muted-foreground">
                      <th className="px-3 py-2">Order</th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2 text-right">Base</th>
                      <th className="px-3 py-2 text-right">Commission</th>
                      <th className="px-3 py-2">Via</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.commission_id} className="border-b border-[#F0F0F0]" data-testid={`commission-row-${c.commission_id}`}>
                        <td className="px-3 py-2 font-mono">{c.order_id}</td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(c.created_at)}</td>
                        <td className="px-3 py-2 text-right">{fmtINR(c.commission_base)}</td>
                        <td className="px-3 py-2 text-right font-bold">{fmtINR(c.commission_amount)}</td>
                        <td className="px-3 py-2 text-[10px] uppercase">{c.attributed_via}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === 'paid' ? 'bg-green-50 text-green-700' : c.status === 'reversed' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>{c.status}</span>
                        </td>
                        <td className="px-3 py-2">
                          {c.status === 'pending' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markPaid(c.commission_id)} data-testid={`mark-paid-${c.commission_id}`}><DollarSign className="h-3 w-3 mr-1" /> Mark Paid</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminAffiliates;
